import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

import type { RoleTourProfile } from './roleTours';
import styles from './RolePreview.module.css';

/**
 * The internal profiles' counterpart of the buying tour's "produto vivo": three small pieces of
 * the screens this profile works in, one per step, each playing its own action — a ghost cursor
 * types and clicks, numbers roll, a gauge fills. One clock, one loop of 13,5 s, a third per step
 * (`onStep`). The figures are fixed examples, the same for every account. Decorative
 * (`aria-hidden`); stops while the tab is hidden; reduced motion — or no way to ask about it, as in
 * tests — shows the last step, finished.
 */

const LOOP_MS = 13_500;
const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const PERCENT = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

const clamp = (v: number) => Math.min(1, Math.max(0, v));
const seg = (p: number, from: number, to: number) => clamp((p - from) / (to - from));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 4);
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const money = (n: number) => BRL.format(n);
const percent = (n: number) => `${PERCENT.format(n)}%`;

export type RolePreviewStep = 1 | 2 | 3;

/** [progress, x %, y %] of the panel; the cursor glides between consecutive points. */
type CursorPath = readonly (readonly [number, number, number])[];

interface Panel {
  readonly view: () => ReactNode;
  readonly play: (el: HTMLElement, p: number) => void;
}

function find(el: HTMLElement, key: string): HTMLElement | null {
  return el.querySelector<HTMLElement>(`[data-rp="${key}"]`);
}

function findAll(el: HTMLElement, key: string): HTMLElement[] {
  return [...el.querySelectorAll<HTMLElement>(`[data-rp="${key}"]`)];
}

function flag(el: HTMLElement | null, attribute: string, on: boolean) {
  el?.toggleAttribute(attribute, on);
}

function write(el: HTMLElement | null, text: string) {
  if (el !== null && el.textContent !== text) {
    el.textContent = text;
  }
}

/** Types `text` into a fake input, `q` of the way through, with a caret while it types. */
function typeInto(el: HTMLElement | null, text: string, q: number) {
  write(el, text.slice(0, Math.round(text.length * q)));
  flag(el, 'data-typing', q > 0 && q < 1);
}

function moveCursor(el: HTMLElement, p: number, path: CursorPath, until: number) {
  const cursor = find(el, 'cursor');
  const first = path[0];
  if (cursor === null || first === undefined) {
    return;
  }
  let from = first;
  let to = first;
  path.forEach((point, index) => {
    if (point[0] <= p) {
      from = point;
      to = path[index + 1] ?? point;
    }
  });
  const t = to === from ? 1 : easeInOut(seg(p, from[0], to[0]));
  const x = ((from[1] + (to[1] - from[1]) * t) / 100) * el.clientWidth;
  const y = ((from[2] + (to[2] - from[2]) * t) / 100) * el.clientHeight;
  cursor.style.transform = `translate(${String(x)}px, ${String(y)}px)`;
  flag(cursor, 'data-show', p >= first[0] && p < until);
}

function Cursor() {
  return (
    <svg className={styles.cursor} data-rp="cursor" viewBox="0 0 18 22">
      <path d="M2 1l13 11-6 1 3.5 7-2.6 1.2-3.4-7L2 18z" />
    </svg>
  );
}

function Head({ title, note }: { readonly title: string; readonly note: string }) {
  return (
    <div className={styles.head}>
      <b>{title}</b>
      <span>{note}</span>
    </div>
  );
}

function Gauge({ id, tone = 'progress' }: { readonly id: string; readonly tone?: string }) {
  return (
    <div className={styles.gauge} data-tone={tone}>
      <span className={styles.gaugePercent} data-rp={`${id}-pc`}>
        0%
      </span>
      <span className={styles.gaugeLabel}>Mínimo</span>
      <span className={styles.gaugeTrack}>
        <span className={styles.gaugeFill} data-rp={`${id}-fill`} />
      </span>
      <span className={styles.gaugeTick} />
    </div>
  );
}

function fillGauge(el: HTMLElement, id: string, attainment: number, t: number) {
  const fill = find(el, `${id}-fill`);
  if (fill !== null) {
    fill.style.transform = `scaleX(${String((attainment / 125) * t)})`;
  }
  write(find(el, `${id}-pc`), percent(attainment * t));
}

const grid = (columns: string): CSSProperties => ({ gridTemplateColumns: columns });

/* ---------- Compras ---------- */

const PROCUREMENT: readonly Panel[] = [
  {
    view: () => (
      <>
        <Head title="Novo produto" note="Cadastro › Produtos" />
        <span className={styles.field}>
          Código
          <span className={`${styles.input} ${styles.mono}`} data-rp="code" />
        </span>
        <span className={styles.field}>
          Descrição
          <span className={styles.input} data-rp="description" />
        </span>
        <div className={styles.pair}>
          <span className={styles.field}>
            Unidade base
            <span className={styles.input} data-rp="unit" data-empty="">
              Selecione
            </span>
          </span>
          <span className={styles.field}>
            Categoria
            <span className={styles.input} data-rp="category" data-empty="">
              Selecione
            </span>
          </span>
        </div>
        <div className={styles.foot}>
          <span className={styles.button} data-rp="save">
            Salvar produto
          </span>
          <span className={styles.chip} data-tone="success" data-rp="saved" data-pop="">
            Produto cadastrado
          </span>
        </div>
      </>
    ),
    play: (el, p) => {
      typeInto(find(el, 'code'), 'ARR-5KG', seg(p, 0.08, 0.24));
      typeInto(find(el, 'description'), 'Arroz agulhinha tipo 1', seg(p, 0.28, 0.5));
      const unit = find(el, 'unit');
      write(unit, p > 0.6 ? 'kg' : 'Selecione');
      flag(unit, 'data-empty', p <= 0.6);
      const category = find(el, 'category');
      write(category, p > 0.68 ? 'Mercearia' : 'Selecione');
      flag(category, 'data-empty', p <= 0.68);
      flag(find(el, 'save'), 'data-press', p > 0.78 && p < 0.82);
      flag(find(el, 'saved'), 'data-in', p > 0.82);
      moveCursor(
        el,
        p,
        [
          [0.02, 92, 96],
          [0.06, 40, 27],
          [0.26, 40, 44],
          [0.52, 26, 65],
          [0.62, 74, 65],
          [0.72, 18, 88],
          [0.8, 18, 88],
          [0.9, 60, 104],
        ],
        0.9,
      );
    },
  },
  {
    view: () => (
      <>
        <Head title="Custo de aquisição" note="ARR-5KG · Distribuidora Aurora" />
        <div className={styles.ladder}>
          {(
            [
              ['A partir de 0 kg', 100, 5.2],
              ['500 kg', 93, 4.85],
              ['1.000 kg', 88, 4.6],
            ] as const
          ).map(([from, width, cost]) => (
            <div key={from} className={styles.rung} data-rp="rung">
              <span>{from}</span>
              <span className={styles.rungBar} style={{ inlineSize: `${String(width)}%` }} />
              <b>{money(cost)}/kg</b>
            </div>
          ))}
        </div>
        <p className={styles.note}>Vigente a partir de 01/10/2026.</p>
        <div className={styles.foot}>
          <span className={styles.button} data-variant="secondary" data-rp="add">
            Adicionar faixa
          </span>
          <span className={styles.chip} data-tone="success" data-rp="cut" data-pop="">
            −11,5% na maior faixa
          </span>
        </div>
      </>
    ),
    play: (el, p) => {
      findAll(el, 'rung').forEach((rung, index) => {
        flag(rung, 'data-in', p > ([0.08, 0.38, 0.66][index] ?? 1));
      });
      flag(find(el, 'add'), 'data-press', (p > 0.33 && p < 0.37) || (p > 0.61 && p < 0.65));
      flag(find(el, 'cut'), 'data-in', p > 0.74);
      moveCursor(
        el,
        p,
        [
          [0.2, 70, 100],
          [0.3, 18, 88],
          [0.58, 18, 88],
          [0.7, 50, 104],
        ],
        0.7,
      );
    },
  },
  {
    view: () => (
      <>
        <Head title="Tabela de preço" note="Restaurante Sabor da Serra" />
        <div className={styles.table}>
          <div className={styles.row} data-head="" style={grid('minmax(0,1fr) 4rem 3rem 4.75rem')}>
            <span>Produto</span>
            <span className={styles.end}>Custo</span>
            <span className={styles.end}>Spread</span>
            <span className={styles.end}>Preço</span>
          </div>
          <div className={styles.row} style={grid('minmax(0,1fr) 4rem 3rem 4.75rem')}>
            <span>Arroz agulhinha</span>
            <span className={styles.end}>{money(5.2)}</span>
            <span className={styles.end} data-rp="spread" data-pop="">
              18%
            </span>
            <b className={styles.end} data-rp="price">
              {money(5.2)}
            </b>
          </div>
        </div>
        <p className={styles.note}>Custo × (1 + spread). Quem publica é o Backoffice comercial.</p>
        <div className={styles.foot}>
          <span className={styles.chip} data-tone="success" data-rp="published" data-pop="">
            Publicada · vale a partir de 01/10
          </span>
        </div>
      </>
    ),
    play: (el, p) => {
      flag(find(el, 'spread'), 'data-in', p > 0.14);
      write(find(el, 'price'), money(5.2 + (6.14 - 5.2) * easeOut(seg(p, 0.22, 0.58))));
      flag(find(el, 'published'), 'data-in', p > 0.72);
    },
  },
];

/* ---------- Backoffice comercial ---------- */

const SPREAD_COLUMNS = grid('minmax(0,1fr) minmax(0,1fr) 3.5rem 4.5rem');
const PRICE_ROWS = [
  ['Arroz agulhinha tipo 1', 5.2, 6.14],
  ['Tomate italiano', 5.92, 6.99],
  ['Leite integral', 3.64, 4.3],
] as const;

const COMMERCIAL: readonly Panel[] = [
  {
    view: () => (
      <>
        <Head title="Regras de spread" note="Preços" />
        <div className={styles.table}>
          <div className={styles.row} data-head="" style={SPREAD_COLUMNS}>
            <span>Categoria</span>
            <span>Cliente</span>
            <span className={styles.end}>Spread</span>
            <span>Situação</span>
          </div>
          <div className={styles.row} style={SPREAD_COLUMNS}>
            <span>Regra geral</span>
            <span className={styles.muted}>Todos</span>
            <span className={styles.end}>15%</span>
            <span className={styles.chip} data-tone="success">
              Vigente
            </span>
          </div>
          <div className={styles.row} style={SPREAD_COLUMNS} data-rp="new-row" data-new="">
            <span className={styles.input} data-size="sm" data-rp="category" />
            <span className={styles.input} data-size="sm" data-rp="customer" />
            <span className={`${styles.input} ${styles.end}`} data-size="sm" data-rp="spread" />
            <span className={styles.chip} data-tone="success" data-rp="valid" data-pop="">
              Vigente
            </span>
          </div>
        </div>
        <div className={styles.foot}>
          <span className={styles.button} data-variant="secondary" data-rp="new">
            Nova regra
          </span>
          <span className={styles.button} data-rp="save">
            Salvar
          </span>
        </div>
      </>
    ),
    play: (el, p) => {
      flag(find(el, 'new'), 'data-press', p > 0.07 && p < 0.11);
      const row = find(el, 'new-row');
      flag(row, 'data-in', p > 0.1);
      flag(row, 'data-fresh', p > 0.1 && p < 0.9);
      typeInto(find(el, 'category'), 'Mercearia', seg(p, 0.14, 0.3));
      typeInto(find(el, 'customer'), 'Sabor da Serra', seg(p, 0.33, 0.5));
      typeInto(find(el, 'spread'), '18%', seg(p, 0.53, 0.6));
      flag(find(el, 'save'), 'data-press', p > 0.7 && p < 0.74);
      flag(find(el, 'valid'), 'data-in', p > 0.75);
      moveCursor(
        el,
        p,
        [
          [0, 80, 104],
          [0.06, 18, 88],
          [0.11, 18, 88],
          [0.14, 22, 64],
          [0.31, 50, 64],
          [0.51, 76, 64],
          [0.64, 48, 88],
          [0.74, 48, 88],
          [0.84, 70, 106],
        ],
        0.84,
      );
    },
  },
  {
    view: () => (
      <>
        <Head title="Tabela de preço" note="Restaurante Sabor da Serra" />
        <div className={styles.table}>
          <div className={styles.row} data-head="" style={grid('minmax(0,1fr) 4rem 4.75rem')}>
            <span>Produto</span>
            <span className={styles.end}>Custo</span>
            <span className={styles.end}>Preço</span>
          </div>
          {PRICE_ROWS.map(([name, cost]) => (
            <div key={name} className={styles.row} style={grid('minmax(0,1fr) 4rem 4.75rem')}>
              <span>{name}</span>
              <span className={`${styles.end} ${styles.muted}`}>{money(cost)}</span>
              <b className={styles.end} data-rp="price">
                {money(cost)}
              </b>
            </div>
          ))}
        </div>
        <div className={styles.foot}>
          <span className={styles.button} data-rp="publish">
            Publicar
          </span>
          <span className={styles.chip} data-tone="success" data-rp="published" data-pop="">
            Publicada · vale a partir de 01/10
          </span>
        </div>
      </>
    ),
    play: (el, p) => {
      findAll(el, 'price').forEach((cell, index) => {
        const [, cost, price] = PRICE_ROWS[index] ?? PRICE_ROWS[0];
        const t = easeOut(seg(p, 0.14 + index * 0.1, 0.44 + index * 0.1));
        write(cell, money(cost + (price - cost) * t));
      });
      flag(find(el, 'publish'), 'data-press', p > 0.7 && p < 0.74);
      flag(find(el, 'published'), 'data-in', p > 0.75);
      moveCursor(
        el,
        p,
        [
          [0.55, 80, 104],
          [0.66, 14, 88],
          [0.74, 14, 88],
          [0.84, 40, 106],
        ],
        0.84,
      );
    },
  },
  {
    view: () => (
      <>
        <Head title="Entregas" note="Logística" />
        <div className={styles.table}>
          <div className={styles.row} style={grid('4.5rem minmax(0,1fr) 5.25rem')}>
            <span className={styles.mono}>PED-1042</span>
            <span>Sabor da Serra</span>
            <span className={styles.chip} data-rp="first">
              Separado
            </span>
          </div>
          <div className={styles.row} style={grid('4.5rem minmax(0,1fr) 5.25rem')}>
            <span className={styles.mono}>PED-1043</span>
            <span>Bistrô Vila Nova</span>
            <span className={styles.chip} data-rp="second">
              Confirmado
            </span>
          </div>
        </div>
        <div className={styles.contract}>
          <b>CT-2026-014 · Sabor da Serra</b>
          <Gauge id="contract" />
        </div>
      </>
    ),
    play: (el, p) => {
      const first = find(el, 'first');
      write(first, p > 0.22 ? 'A caminho' : 'Separado');
      first?.setAttribute('data-tone', p > 0.22 ? 'info' : 'neutral');
      write(find(el, 'second'), p > 0.42 ? 'Separado' : 'Confirmado');
      fillGauge(el, 'contract', 73, easeOut(seg(p, 0.52, 0.82)));
    },
  },
];

/* ---------- Controladoria ---------- */

const PAYMENTS = [
  ['PED-1042', 'Boleto', 1240.8],
  ['PED-1043', 'Boleto', 860],
  ['PED-1044', 'Boleto', 312.4],
] as const;
const PAID_AT = [0.18, 0.4, 0.62] as const;
const MATCH_COLUMNS = grid('3.25rem minmax(0,1fr) minmax(0,1fr) 1.25rem');
const KPIS = [
  ['GMV', 48391.82, money],
  ['Pedidos', 214, (n: number) => String(Math.round(n))],
  ['OTIF', 91.2, percent],
  ['Ticket médio', 226.13, money],
  ['Conversão', 38.4, percent],
  ['Adoção', 62.5, percent],
] as const;

const CONTROLLERSHIP: readonly Panel[] = [
  {
    view: () => (
      <>
        <Head title="Títulos a receber" note="Hoje" />
        <div className={styles.table}>
          {PAYMENTS.map(([order, method, amount]) => (
            <div key={order} className={styles.row} style={grid('minmax(0,1fr) auto 5.25rem')}>
              <span>
                <span className={styles.mono}>{order}</span>{' '}
                <span className={styles.muted}>· {method}</span>
              </span>
              <span className={styles.end}>{money(amount)}</span>
              <span className={styles.chip} data-rp="status">
                Aguardando
              </span>
            </div>
          ))}
        </div>
        <div className={styles.foot} data-spread="">
          <span className={styles.muted}>Recebido hoje</span>
          <b className={styles.figure} data-rp="sum">
            {money(0)}
          </b>
        </div>
      </>
    ),
    play: (el, p) => {
      let sum = 0;
      findAll(el, 'status').forEach((chip, index) => {
        const at = PAID_AT[index] ?? 1;
        write(chip, p > at ? 'Pago' : 'Aguardando');
        chip.setAttribute('data-tone', p > at ? 'success' : 'neutral');
        sum += (PAYMENTS[index]?.[2] ?? 0) * easeOut(seg(p, at, at + 0.12));
      });
      write(find(el, 'sum'), money(sum));
    },
  },
  {
    view: () => (
      <>
        <Head title="Conciliação · execução de hoje" note="Local × provedor" />
        <div className={styles.table}>
          <div className={styles.row} data-head="" style={MATCH_COLUMNS}>
            <span>Pedido</span>
            <span className={styles.end}>Local</span>
            <span className={styles.end}>Provedor</span>
            <span />
          </div>
          {(
            [
              ['1042', 1240.8, 1240.8],
              ['1043', 860, 850],
              ['1044', 312.4, 312.4],
            ] as const
          ).map(([order, local, provider]) => (
            <div key={order} className={styles.row} style={MATCH_COLUMNS}>
              <span className={styles.mono}>{order}</span>
              <span className={styles.end}>{money(local)}</span>
              <span className={styles.end} data-differs={local !== provider ? '' : undefined}>
                {money(provider)}
              </span>
              <span
                className={styles.mark}
                data-tone={local === provider ? 'success' : 'warning'}
                data-rp="mark"
              >
                {local === provider ? '✓' : '!'}
              </span>
            </div>
          ))}
        </div>
        <p className={styles.note} data-rp="why" data-pop="">
          <span className={styles.chip} data-tone="warning">
            Divergência
          </span>{' '}
          1043: o provedor confirmou R$ 10,00 a menos. Fica para alguém resolver.
        </p>
        <div className={styles.foot}>
          <span>
            Verificados <b data-rp="checked">0</b>
          </span>
          <span>
            Divergências <b data-rp="differ">0</b>
          </span>
        </div>
      </>
    ),
    play: (el, p) => {
      const at = [0.14, 0.36, 0.58];
      findAll(el, 'mark').forEach((mark, index) => {
        flag(mark, 'data-in', p > (at[index] ?? 1));
      });
      write(find(el, 'checked'), String(at.filter((a) => p > a).length));
      write(find(el, 'differ'), p > 0.36 ? '1' : '0');
      flag(find(el, 'why'), 'data-in', p > 0.4);
    },
  },
  {
    view: () => (
      <>
        <Head title="Indicadores" note="01/09 a 30/09/2026" />
        <dl className={styles.kpis}>
          {KPIS.map(([label]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd data-rp="kpi">0</dd>
            </div>
          ))}
        </dl>
        <p className={styles.note}>Dados de demonstração: não houve venda real.</p>
      </>
    ),
    play: (el, p) => {
      findAll(el, 'kpi').forEach((cell, index) => {
        const kpi = KPIS[index];
        if (kpi !== undefined) {
          write(cell, kpi[2](kpi[1] * easeOut(seg(p, 0.08 + index * 0.04, 0.58 + index * 0.04))));
        }
      });
    },
  },
];

/* ---------- Administrador da plataforma ---------- */

const PALETTE = [
  ['Produtos', 'Cadastro'],
  ['Posição contratual', 'Contratos'],
  ['Prazos de entrega', 'Logística'],
  ['Tabelas de preço', 'Preços'],
  ['Indicadores', 'Financeiro'],
] as const;
const AREAS = [
  ['Cadastro', 4],
  ['Preços', 3],
  ['Contratos', 3],
  ['Logística', 2],
  ['Financeiro', 2],
] as const;
const plain = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/gu, '').toLowerCase();

const PLATFORM_ADMIN: readonly Panel[] = [
  {
    view: () => (
      <>
        <div className={styles.palette} data-rp="palette">
          <span className={styles.paletteInput} data-rp="query" />
          <ul>
            {PALETTE.map(([label, area]) => (
              <li key={label} data-rp="option" data-label={plain(label)}>
                {label}
                <span>{area}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.landing} data-rp="landing">
          <b>Posição contratual</b>
          <Gauge id="landing" />
        </div>
      </>
    ),
    play: (el, p) => {
      const typed = 'posi'.slice(0, Math.round(4 * seg(p, 0.1, 0.34)));
      const query = find(el, 'query');
      write(query, typed === '' ? 'Ir para…' : typed);
      flag(query, 'data-empty', typed === '');
      flag(query, 'data-typing', p < 0.5);
      let first = true;
      findAll(el, 'option').forEach((option) => {
        const hit = (option.dataset.label ?? '').includes(typed);
        option.hidden = !hit;
        flag(option, 'data-highlight', hit && first && typed !== '');
        if (hit) {
          first = false;
        }
      });
      flag(find(el, 'palette'), 'data-gone', p > 0.56);
      flag(find(el, 'landing'), 'data-in', p > 0.6);
      fillGauge(el, 'landing', 73, p > 0.6 ? 1 : 0);
    },
  },
  {
    view: () => (
      <>
        <Head title="Backoffice" note="Suas áreas" />
        <div className={styles.areas}>
          {AREAS.map(([area, screens]) => (
            <div key={area} data-rp="area">
              {area}
              <span>{screens} telas</span>
            </div>
          ))}
        </div>
      </>
    ),
    play: (el, p) => {
      findAll(el, 'area').forEach((area, index) => {
        const from = 0.08 + index * 0.15;
        flag(area, 'data-on', (p > from && p < from + 0.15) || (index === 4 && p > 0.68));
      });
      moveCursor(
        el,
        p,
        [
          [0.04, 90, 100],
          [0.08, 60, 32],
          [0.23, 60, 47],
          [0.38, 60, 62],
          [0.53, 60, 77],
          [0.68, 60, 92],
          [0.8, 92, 104],
        ],
        0.8,
      );
    },
  },
  {
    view: () => (
      <>
        <Head title="Visão geral" note="Setembro de 2026" />
        <dl className={styles.kpis} data-columns="2">
          <div>
            <dt>GMV</dt>
            <dd data-rp="gmv">{money(0)}</dd>
          </div>
          <div>
            <dt>OTIF</dt>
            <dd data-rp="otif">0%</dd>
          </div>
        </dl>
        <div className={styles.contract}>
          <b>Sabor da Serra · CT-2026-014</b>
          <Gauge id="first" />
        </div>
        <div className={styles.contract}>
          <b>Bistrô Vila Nova · CT-2026-009</b>
          <Gauge id="second" tone="warning" />
        </div>
      </>
    ),
    play: (el, p) => {
      write(find(el, 'gmv'), money(48391.82 * easeOut(seg(p, 0.08, 0.58))));
      write(find(el, 'otif'), percent(91.2 * easeOut(seg(p, 0.12, 0.62))));
      fillGauge(el, 'first', 73, easeOut(seg(p, 0.35, 0.75)));
      fillGauge(el, 'second', 34.58, easeOut(seg(p, 0.4, 0.8)));
    },
  },
];

const SCENES: Readonly<Record<RoleTourProfile, readonly Panel[]>> = {
  procurement: PROCUREMENT,
  commercial_backoffice: COMMERCIAL,
  controllership: CONTROLLERSHIP,
  platform_admin: PLATFORM_ADMIN,
};

function canAnimate(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function animate(
  root: HTMLElement,
  panels: readonly Panel[],
  onStep: (step: RolePreviewStep) => void,
): () => void {
  const elements = [...root.querySelectorAll<HTMLElement>('[data-rp-panel]')];
  let step: RolePreviewStep | 0 = 0;
  const paint = (c: number) => {
    const current = Math.min(2, Math.floor(c * 3));
    const p = clamp(((c * 3) % 1) / 0.9);
    elements.forEach((el, index) => {
      panels[index]?.play(el, index < current ? 1 : index > current ? 0 : p);
      flag(el, 'data-on', index === current);
    });
    const next = (current + 1) as RolePreviewStep;
    if (next !== step) {
      step = next;
      onStep(next);
    }
  };

  if (!canAnimate()) {
    paint(0.999);
    return () => undefined;
  }

  let frame = 0;
  let elapsed = 0;
  let last = performance.now();
  const tick = (now: number) => {
    elapsed += now - last;
    last = now;
    paint((elapsed % LOOP_MS) / LOOP_MS);
    frame = requestAnimationFrame(tick);
  };
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      cancelAnimationFrame(frame);
    } else {
      last = performance.now();
      frame = requestAnimationFrame(tick);
    }
  };

  frame = requestAnimationFrame(tick);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    cancelAnimationFrame(frame);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

export interface RolePreviewProps {
  readonly profile: RoleTourProfile;
  readonly onStep?: ((step: RolePreviewStep) => void) | undefined;
  readonly className?: string | undefined;
}

export function RolePreview({ profile, onStep, className }: RolePreviewProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const stepRef = useRef(onStep);
  const panels = SCENES[profile];

  useEffect(() => {
    stepRef.current = onStep;
  }, [onStep]);

  useEffect(() => {
    const root = rootRef.current;
    if (root === null) {
      return undefined;
    }
    return animate(root, panels, (step) => {
      stepRef.current?.(step);
    });
  }, [panels]);

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      ref={rootRef}
      aria-hidden="true"
    >
      <div className={styles.stage}>
        <div className={styles.deck}>
          {panels.map((panel, index) => (
            <div key={index} className={styles.panel} data-rp-panel="">
              {panel.view()}
              <Cursor />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
